'use client';

import { c as _c } from "react/compiler-runtime";
import { useActiveWallet, useWallets } from '@morfi/backend-sdk-v2/client';
import { useTranslations } from 'next-intl';
import { useTheme } from 'next-themes';
import { type ReactNode, useState } from 'react';
import { QRCode } from 'react-qrcode-logo';
import { LOGO_SRC_DARK_BACKGROUND, LOGO_SRC_LIGHT_BACKGROUND, logoWordmarkWidthForHeight } from '@/components/logo';
import { FundingHistoryEntry } from '@/components/onramp/funding-receipt-link';
import { AnimatedButton } from '@/components/ui/animated-button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuRadioGroup, DropdownMenuRadioItem, DropdownMenuTrigger } from '@/components/ui/app-dropdown-menu';
import { Skeleton } from '@/components/ui/app-skeleton';
import { Icon } from '@/components/ui/icon';
import { ArrowDown01Icon, Copy01Icon } from '@/components/ui/icon/glyphs';
import { ModalFooter } from '@/components/ui/modal-footer';
import { SilkSheet } from '@/components/ui/silk-sheet';
import { walletDisplayLabel } from '@/components/wallet/wallet-display-label';
import { getUsdcMint } from '@/config/solana';
import { useHydrated } from '@/hooks/ui/use-hydrated';
import { useFormattedCurrency } from '@/lib/currency';
import { getActiveNetwork } from '@/lib/network/active';
import { copyWalletAddress } from '@/lib/wallet/copy-address';
import { useMultiWalletFeature } from '@/providers/multi-wallet-feature-provider';
import { useWalletBalances } from '@/queries/wallet-balance';
import type { Wallet } from '@/types/auth';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const QR_SIZE = 200;
const QR_LOGO_HEIGHT: number = 40;
const QR_LOGO_WIDTH: number = logoWordmarkWidthForHeight(QR_LOGO_HEIGHT);
const QR_LOGO_PADDING = 4;
const QR_COLORS = {
  light: {
    fg: '#0a0a0a',
    bg: '#ffffff'
  },
  dark: {
    fg: '#fafafa',
    bg: '#0a0a0a'
  }
} as const;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function buildSolanaPayUri(address: string): string {
  return `solana:${address}?spl-token=${getUsdcMint()}`;
}
function shortenWalletAddress(address: string): string {
  if (address.length <= 12) return address;
  return `${address.slice(0, 6)}…${address.slice(-4)}`;
}
interface WalletQrPickerItemProps {
  wallet: Wallet;
  balanceVisual: ReactNode;
}
function WalletQrPickerItem(t0) {
  const $ = _c(15);
  const {
    wallet,
    balanceVisual
  } = t0;
  let t1;
  if ($[0] !== wallet) {
    t1 = walletDisplayLabel(wallet);
    $[0] = wallet;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const label = t1;
  const showSecondaryAddress = wallet.label.length > 0;
  let t2;
  if ($[2] !== label) {
    t2 = <span className="truncate text-body font-medium text-foreground-primary">{label}</span>;
    $[2] = label;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== showSecondaryAddress || $[5] !== wallet.address) {
    t3 = showSecondaryAddress ? <span className="truncate font-mono text-caption text-foreground-secondary">{shortenWalletAddress(wallet.address)}</span> : null;
    $[4] = showSecondaryAddress;
    $[5] = wallet.address;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t2 || $[8] !== t3) {
    t4 = <span className="flex min-w-0 flex-1 flex-col text-left leading-tight">{t2}{t3}</span>;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== balanceVisual) {
    t5 = <span className="shrink-0 whitespace-nowrap text-right text-caption tabular-nums text-foreground-secondary">{balanceVisual}</span>;
    $[10] = balanceVisual;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t4 || $[13] !== t5) {
    t6 = <span className="flex min-w-0 w-full items-center gap-3">{t4}{t5}</span>;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  return t6;
}
interface WalletQrPickerEntry extends WalletQrPickerItemProps {
  accessibleLabel: string;
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface WalletQrDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function WalletQrDialog(t0) {
  const $ = _c(41);
  const {
    open,
    onOpenChange
  } = t0;
  const t = useTranslations("profile.qr");
  const fundingText = useTranslations("onramp");
  const [historyOpen, setHistoryOpen] = useState(false);
  const {
    formatD6
  } = useFormattedCurrency();
  const {
    resolvedTheme
  } = useTheme();
  const mounted = useHydrated();
  const {
    activeWallet
  } = useActiveWallet();
  const {
    wallets
  } = useWallets();
  const {
    enabled: multiWalletEnabled,
    primaryWallet
  } = useMultiWalletFeature();
  const [requestedSelectedWalletId, setSelectedWalletId] = useState("");
  const scopedWallets = multiWalletEnabled ? wallets : primaryWallet === null ? [] : [primaryWallet];
  const selectableWallets = scopedWallets.filter(_temp);
  const defaultWallet = selectableWallets.find(wallet_0 => wallet_0.id === activeWallet?.id) ?? selectableWallets[0];
  const selectedWallet = selectableWallets.find(wallet_1 => wallet_1.id === requestedSelectedWalletId) ?? defaultWallet;
  const selectedWalletId = selectedWallet?.id ?? "";
  const activeWallets = scopedWallets.filter(_temp2);
  const walletAddresses = open ? activeWallets.map(_temp3) : [];
  const {
    perWalletRaw,
    isLoading: balancesLoading,
    isError: balancesError
  } = useWalletBalances(walletAddresses);
  let t1;
  if ($[0] !== balancesError || $[1] !== balancesLoading || $[2] !== formatD6 || $[3] !== perWalletRaw || $[4] !== t) {
    t1 = wallet_4 => {
      const balanceD6 = perWalletRaw.get(wallet_4.address);
      const balanceAccessible = balancesLoading ? t("balance_loading") : balancesError || balanceD6 === undefined ? t("balance_unavailable") : formatD6(balanceD6.toString());
      const balanceVisual = balancesLoading ? <Skeleton key={wallet_4.id} className="h-4 w-16" /> : balancesError || balanceD6 === undefined ? formatD6("0") : balanceAccessible;
      const label = walletDisplayLabel(wallet_4);
      return {
        wallet: wallet_4,
        balanceVisual,
        accessibleLabel: t("wallet_option_accessible", {
          label,
          address: wallet_4.address,
          balance: balanceAccessible
        })
      };
    };
    $[0] = balancesError;
    $[1] = balancesLoading;
    $[2] = formatD6;
    $[3] = perWalletRaw;
    $[4] = t;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const walletItems = activeWallets.map(t1);
  const selectedWalletItem = walletItems.find(item => item.wallet.id === selectedWalletId);
  const isDark = mounted && resolvedTheme === "dark";
  const colors = isDark ? QR_COLORS.dark : QR_COLORS.light;
  let t2;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = getActiveNetwork();
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const isMainnet = t2 === "mainnet";
  let t3;
  if ($[7] !== t) {
    t3 = t("network_devnet");
    $[7] = t;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const networkLabel = t3;
  const handleCopy = function handleCopy() {
    if (selectedWallet !== undefined) {
      copyWalletAddress(selectedWallet.address, selectedWallet.label);
    }
  };
  let t4;
  if ($[9] !== onOpenChange) {
    t4 = function handleOpenChange(nextOpen) {
      if (!nextOpen) {
        setSelectedWalletId("");
      }
      onOpenChange(nextOpen);
    };
    $[9] = onOpenChange;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const handleOpenChange = t4;
  const T0 = SilkSheet;
  const t5 = open && !historyOpen;
  const t6 = "detached";
  const t7 = "sm";
  let t8;
  if ($[11] !== t) {
    t8 = t("dialog_title");
    $[11] = t;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== t) {
    t9 = t("dialog_description");
    $[13] = t;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  const t10 = selectedWallet !== undefined ? <div className="flex flex-col gap-2">{multiWalletEnabled ? <DropdownMenu><DropdownMenuTrigger asChild={true}><AnimatedButton type="button" variant="secondary" className="h-auto w-full justify-between border bg-background-primary px-3 py-2.5 text-left" aria-label={t("dialog_title")}><WalletQrPickerItem wallet={selectedWallet} balanceVisual={selectedWalletItem?.balanceVisual ?? formatD6("0")} /><Icon icon={ArrowDown01Icon} className="size-4 shrink-0 text-foreground-secondary" aria-hidden="true" /></AnimatedButton></DropdownMenuTrigger><DropdownMenuContent align="start" className="max-h-56 w-[var(--radix-dropdown-menu-trigger-width)] overflow-y-auto p-1"><DropdownMenuRadioGroup value={selectedWalletId} onValueChange={setSelectedWalletId}>{walletItems.map(_temp4)}</DropdownMenuRadioGroup></DropdownMenuContent></DropdownMenu> : <div className="rounded-control border bg-background-primary px-3 py-2.5"><WalletQrPickerItem wallet={selectedWallet} balanceVisual={selectedWalletItem?.balanceVisual ?? formatD6("0")} /></div>}<ModalFooter><AnimatedButton variant="secondary" className="w-full sm:w-auto" onClick={handleCopy}><Icon icon={Copy01Icon} className="size-4" aria-hidden="true" />{t("copy_address")}</AnimatedButton></ModalFooter></div> : undefined;
  let t11;
  if ($[15] !== colors || $[16] !== isDark || $[17] !== mounted || $[18] !== networkLabel || $[19] !== selectedWallet) {
    t11 = selectedWallet !== undefined && <div className="flex flex-col gap-4 py-2">{!isMainnet && <span className="inline-flex self-center items-center rounded-full bg-amber-500/10 px-2.5 py-0.5 text-caption font-medium text-amber-600 dark:text-amber-400">{networkLabel}</span>}<div className="self-center rounded-overlay border bg-background-secondary p-4">{mounted ? <QRCode value={buildSolanaPayUri(selectedWallet.address)} size={QR_SIZE} ecLevel="M" qrStyle="dots" eyeRadius={8} fgColor={colors.fg} bgColor={colors.bg} logoImage={isDark ? LOGO_SRC_DARK_BACKGROUND : LOGO_SRC_LIGHT_BACKGROUND} logoWidth={QR_LOGO_WIDTH} logoHeight={QR_LOGO_HEIGHT} logoPadding={QR_LOGO_PADDING} logoPaddingStyle="circle" removeQrCodeBehindLogo={true} quietZone={0} /> : <div style={{
          width: QR_SIZE,
          height: QR_SIZE
        }} className="animate-pulse rounded-card bg-background-tertiary" />}</div><p className="w-full break-all rounded-overlay-item bg-background-tertiary px-3 py-2 text-center font-mono text-micro text-foreground-primary">{selectedWallet.address}</p></div>;
    $[15] = colors;
    $[16] = isDark;
    $[17] = mounted;
    $[18] = networkLabel;
    $[19] = selectedWallet;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = () => setHistoryOpen(true);
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== fundingText) {
    t13 = fundingText("view_history");
    $[22] = fundingText;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] !== t13) {
    t14 = <AnimatedButton variant="tertiary" size="xs" className="w-full text-caption text-foreground-secondary" onClick={t12}>{t13}</AnimatedButton>;
    $[24] = t13;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  let t15;
  if ($[26] !== T0 || $[27] !== handleOpenChange || $[28] !== t10 || $[29] !== t11 || $[30] !== t14 || $[31] !== t5 || $[32] !== t8 || $[33] !== t9) {
    t15 = <T0 open={t5} onOpenChange={handleOpenChange} variant={t6} size={t7} title={t8} description={t9} footer={t10}>{t11}{t14}</T0>;
    $[26] = T0;
    $[27] = handleOpenChange;
    $[28] = t10;
    $[29] = t11;
    $[30] = t14;
    $[31] = t5;
    $[32] = t8;
    $[33] = t9;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  let t16;
  if ($[35] !== historyOpen || $[36] !== open) {
    t16 = open && historyOpen ? <FundingHistoryEntry onClose={() => setHistoryOpen(false)} /> : null;
    $[35] = historyOpen;
    $[36] = open;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  let t17;
  if ($[38] !== t15 || $[39] !== t16) {
    t17 = <>{t15}{t16}</>;
    $[38] = t15;
    $[39] = t16;
    $[40] = t17;
  } else {
    t17 = $[40];
  }
  return t17;
}
function _temp4(item_0) {
  return <DropdownMenuRadioItem key={item_0.wallet.id} value={item_0.wallet.id} disabled={item_0.wallet.isAvailable === false} aria-label={item_0.accessibleLabel}><WalletQrPickerItem wallet={item_0.wallet} balanceVisual={item_0.balanceVisual} /></DropdownMenuRadioItem>;
}
function _temp3(wallet_3) {
  return wallet_3.address;
}
function _temp2(wallet_2) {
  return wallet_2.archivedAt === null;
}
function _temp(wallet) {
  return wallet.archivedAt === null && wallet.isAvailable !== false;
}